import { useQuery } from 'react-query';
import {
  byRoomPrice,
  getBedChoiceForPax,
  getRoomPax,
  getRoomPrice,
  isRetryableError,
} from '@koob/utils';
import { generateRoomKey } from '../logic/generateRoomKey';
import { useTripDesignerConfig } from '../config/TripDesignerConfigProvider';

// Base behaviour is TO's; DMC additionally forwards `tripId` (its own
// `api.getRoomsAvailability` scopes the request to the trip's partner
// permissions with it, read off the store by DMC's own config wrapper — see
// apps/dmc/src/providers/TripDesignerConfigProvider.js). TO never passes
// `tripId`, so `filters.tripId` stays `undefined` for it, same as before this
// hook moved: an `undefined`-valued key is dropped by `qs.stringify` in both
// apps' API layer regardless of `skipNulls`, so the wire request is
// unchanged.
interface HotelRoomAvailabilityParams {
  hotelId?: string;
  startDate?: string;
  endDate?: string;
  roomsComposition?: any[];
  tripId?: string;
  enabled?: boolean;
}

function fitsCapacity(room: any, pax: number) {
  if (!pax) return true;
  const min = room?.minCapacity ?? 0;
  const max = room?.maxCapacity ?? Infinity;
  return pax >= min && pax <= max;
}

// Own inventory has no nested compositions: pick the cheapest capacity-fitting
// room for each slot independently (a room may fill several slots). Each room
// carries the bed configuration that suits the slot (KD-3732) — left to its
// first one, a room of 3 was suggested without its extra bed — and rooms are
// ranked on that bed's price.
function cheapestPerSlot(roomsComposition: any[], possibilities: any[]) {
  return (roomsComposition ?? [])
    .map((slot) => {
      const pax = getRoomPax(slot);
      return possibilities
        .filter((r) => fitsCapacity(r, pax))
        .map((r) => ({ ...r, bed: getBedChoiceForPax(r, pax) }))
        .sort(byRoomPrice)[0];
    })
    .filter(Boolean);
}

// Two third-party rooms: find the cheapest combination using
// `compatibleRoomCompositions`.
interface RoomPair {
  rooms: any[];
  indices: number[];
}

function cheapestPair(possibilities: any[]): RoomPair | null {
  let bestCombination: RoomPair | null = null;
  let bestTotalPrice = Infinity;

  possibilities.forEach((room1, idx1) => {
    const price1 = getRoomPrice(room1);
    (room1.compatibleRoomCompositions ?? []).forEach(
      (room2: any, idx2: number) => {
        const totalPrice = price1 + getRoomPrice(room2);
        if (totalPrice < bestTotalPrice) {
          bestTotalPrice = totalPrice;
          bestCombination = { rooms: [room1, room2], indices: [idx1, idx2] };
        }
      },
    );
  });

  return bestCombination;
}

// Room + the depth its key was generated at — the pair-combination branch
// keys off each room's own position in `possibilities`, not its position in
// the result array, so the depth has to travel with the room.
function keyedPerSlot(roomsComposition: any[], possibilities: any[]) {
  return cheapestPerSlot(roomsComposition, possibilities).map((room, idx) => ({
    room,
    depth: idx,
  }));
}

function pickCheapestRooms(
  roomsComposition: any[],
  possibilities: any[],
  cheapestRoom: any,
): { room: any; depth: number }[] {
  const roomCount = roomsComposition?.length || 1;

  if (roomCount === 1) {
    return cheapestRoom ? [{ room: cheapestRoom, depth: 0 }] : [];
  }

  const hasCompositions = possibilities.some(
    (p) => p.compatibleRoomCompositions?.length > 0,
  );
  if (roomCount === 2 && hasCompositions && possibilities.length > 0) {
    const bestCombination = cheapestPair(possibilities);
    if (bestCombination) {
      const { rooms, indices } = bestCombination;
      return rooms.map((room, i) => ({ room, depth: indices[i] }));
    }
  }

  // Own inventory (no compositions), 3+ rooms, or no compatible pair found:
  // cheapest capacity-fitting room per slot, duplicates allowed.
  return keyedPerSlot(roomsComposition, possibilities);
}

/**
 * Hotel room availability for a stay: the cheapest room, and the cheapest N
 * rooms for a multi-room composition. Used for automatic room selection and
 * room selection modals in both apps' Trip Designer.
 */
export function useHotelRoomAvailability({
  hotelId,
  startDate,
  endDate,
  roomsComposition,
  tripId,
  enabled = true,
}: HotelRoomAvailabilityParams) {
  const { api, locale } = useTripDesignerConfig();

  return useQuery(
    ['hotelRooms', hotelId, startDate, endDate, roomsComposition, locale],
    async () => {
      // Sanitize roomsComposition to ensure firstAdultNationality is always a string.
      const sanitizedRooms = roomsComposition?.map((room) => ({
        ...room,
        firstAdultNationality:
          typeof room?.firstAdultNationality === 'string'
            ? room.firstAdultNationality
            : (room?.firstAdultNationality?.value ?? ''),
      }));

      const { data } = await api.getRoomsAvailability({
        id: hotelId as string,
        locale,
        filters: {
          startAt: startDate,
          endAt: endDate,
          rooms: sanitizedRooms,
          tripId,
        },
      });

      const possibilities: any[] = (data as any)?.possibilities ?? [];
      const roomCount = roomsComposition?.length || 1;

      // Find cheapest room on the price the room selection quotes: a bed
      // choice carrying a supplement is priced without its promo leg, and
      // reading `totalPriceWithPromo` raw made the suggestion disagree with
      // the modal.
      const cheapestRoom = possibilities.slice().sort(byRoomPrice)[0];
      const keyedCheapestRooms = pickCheapestRooms(
        roomsComposition ?? [],
        possibilities,
        cheapestRoom,
      );
      const cheapestRooms = keyedCheapestRooms.map(({ room }) => room);
      const cheapestRoomsKeys = keyedCheapestRooms
        .map(({ room, depth }) =>
          generateRoomKey(room, depth, room.bed?.id ?? null),
        )
        .join(',');

      return {
        possibilities,
        cheapestRoom, // Keep for backward compatibility
        cheapestRoomKey: cheapestRoom ? generateRoomKey(cheapestRoom, 0) : null,
        cheapestRooms,
        cheapestRoomsKeys,
        roomCount,
        expiresAt: (data as any)?.expiresAt,
        requestId: (data as any)?.requestId,
      };
    },
    {
      enabled: enabled && !!hotelId && !!startDate && !!endDate,
      staleTime: 0, // Always refetch when dates change
      cacheTime: 10 * 60 * 1000, // 10 minutes
      retry: (failureCount: number, error: unknown) =>
        failureCount < 2 && isRetryableError(error),
      retryDelay: (attemptIndex: number) =>
        Math.min(1000 * 2 ** attemptIndex, 10000),
      refetchOnMount: true,
      // KD-3594 D4 — a requestId expires ~20 min after it is issued; the
      // booking then fails with "Request not found or expired". Refetching
      // right as it expires replaces it silently, before that can happen, so
      // the trip never carries a dead id into checkout.
      refetchInterval: (data) => {
        const expiresAt = (data as { expiresAt?: string } | undefined)
          ?.expiresAt;
        if (!expiresAt) return false;
        return Math.max(new Date(expiresAt).getTime() - Date.now(), 1000);
      },
      refetchIntervalInBackground: true,
    },
  );
}
